import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { X, Loader2, Upload, Trash2 } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-hot-toast';
import cloudinaryService from '../../services/cloudinary';

const AddEditPujaModal = ({ isOpen, onClose, onSave, puja, categories }) => {
    const { register, handleSubmit, control, reset, setValue, formState: { errors } } = useForm();
    const [loading, setLoading] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');

    useEffect(() => {
        if (puja) {
            setValue('name', puja.name);
            setValue('categoryId', puja.categoryId);
            setValue('subtitle', puja.subtitle);
            setValue('description', puja.description);
            setValue('benefits', puja.benefits);
            setValue('process', puja.process);
            setValue('includeItems', puja.includeItems);
            setValue('price', puja.price);
            setValue('duration', puja.duration);
            setValue('isActive', puja.isActive);
            setPreviewUrl(puja.image || '');
        } else {
            reset({
                name: '',
                categoryId: '',
                subtitle: '',
                description: '',
                benefits: '',
                process: '',
                includeItems: '',
                price: '',
                duration: '',
                isActive: true
            });
            setPreviewUrl('');
            setImageFile(null);
        }
    }, [puja, setValue, reset, isOpen]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }
            setImageFile(file);
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setPreviewUrl('');
    };

    const onSubmit = async (data) => {
        setLoading(true);
        try {
            let imageUrl = previewUrl;

            if (imageFile) {
                const uploadResult = await cloudinaryService.uploadFile(imageFile, {
                    folder: 'vaidic/pujas',
                    tags: ['puja-service']
                });

                if (!uploadResult.success) {
                    throw new Error(uploadResult.error || 'Failed to upload image');
                }
                imageUrl = uploadResult.url;
            }

            // Convert price to number and add image
            const formattedData = {
                ...data,
                price: data.price ? parseFloat(data.price) : null,
                image: imageUrl
            };

            await onSave(formattedData);
            onClose();
        } catch (error) {
            console.error(error);
            toast.error(error.message || 'Failed to save puja');
        } finally {
            setLoading(false);
        }
    };

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'image'],
            ['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike', 'blockquote',
        'list', 'bullet', 'indent',
        'link', 'image'
    ];

    if (!isOpen) return null;

    return (
        <div translate="no" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                    <h2 className="text-xl font-semibold text-gray-900">
                        {puja ? 'Edit Puja Service' : 'Add Puja Service'}
                    </h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Basic Info */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                                <input
                                    {...register('name', { required: 'Name is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="e.g. Satyanarayana Puja"
                                />
                                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                                <select
                                    {...register('categoryId', { required: 'Category is required' })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                >
                                    <option value="">Select Category</option>
                                    {categories.map((cat) => (
                                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                                    ))}
                                </select>
                                {errors.categoryId && <p className="text-red-500 text-sm mt-1">{errors.categoryId.message}</p>}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                                <input
                                    {...register('subtitle')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                    placeholder="Short tagline"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                                {!previewUrl ? (
                                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-indigo-500 transition-colors cursor-pointer"
                                        onClick={() => document.getElementById('puja-image-upload').click()}
                                    >
                                        <div className="space-y-1 text-center">
                                            <Upload className="mx-auto h-12 w-12 text-gray-400" />
                                            <div className="flex text-sm text-gray-600">
                                                <label className="relative cursor-pointer bg-white rounded-md font-medium text-indigo-600 hover:text-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500">
                                                    <span>Upload a file</span>
                                                    <input
                                                        id="puja-image-upload"
                                                        type="file"
                                                        className="sr-only"
                                                        accept="image/*"
                                                        onChange={handleImageChange}
                                                    />
                                                </label>
                                                <p className="pl-1">or drag and drop</p>
                                            </div>
                                            <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative mt-1 rounded-lg overflow-hidden border border-gray-200 group">
                                        <img
                                            src={previewUrl}
                                            alt="Preview"
                                            className="w-full h-48 object-cover"
                                        />
                                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                                            <button
                                                type="button"
                                                onClick={removeImage}
                                                className="p-2 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                                                title="Remove image"
                                            >
                                                <Trash2 className="w-5 h-5" />
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹)</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        {...register('price')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="0.00"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                                    <input
                                        {...register('duration')}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="e.g. 2 hours"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center pt-4">
                                <input
                                    type="checkbox"
                                    id="isActive"
                                    {...register('isActive')}
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                                    Active
                                </label>
                            </div>
                        </div>

                        {/* Rich Text Editors */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 [&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!border-b [&_.ql-toolbar]:!border-gray-200 [&_.ql-toolbar]:rounded-t-lg [&_.ql-container]:!border-0 [&_.ql-container]:rounded-b-lg [&_.ql-container]:h-48 [&_.ql-editor]:overflow-y-auto">
                                    <Controller
                                        name="description"
                                        control={control}
                                        render={({ field }) => (
                                            <ReactQuill
                                                theme="snow"
                                                value={field.value || ''}
                                                onChange={field.onChange}
                                                modules={modules}
                                                formats={formats}
                                                className="h-full"
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-4 mt-8">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Benefits</label>
                            <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 [&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!border-b [&_.ql-toolbar]:!border-gray-200 [&_.ql-toolbar]:rounded-t-lg [&_.ql-container]:!border-0 [&_.ql-container]:rounded-b-lg [&_.ql-container]:h-48 [&_.ql-editor]:overflow-y-auto">
                                <Controller
                                    name="benefits"
                                    control={control}
                                    render={({ field }) => (
                                        <ReactQuill
                                            theme="snow"
                                            value={field.value || ''}
                                            onChange={field.onChange}
                                            modules={modules}
                                            formats={formats}
                                            className="h-full"
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Process / Vidhi</label>
                            <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 [&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!border-b [&_.ql-toolbar]:!border-gray-200 [&_.ql-toolbar]:rounded-t-lg [&_.ql-container]:!border-0 [&_.ql-container]:rounded-b-lg [&_.ql-container]:h-48 [&_.ql-editor]:overflow-y-auto">
                                <Controller
                                    name="process"
                                    control={control}
                                    render={({ field }) => (
                                        <ReactQuill
                                            theme="snow"
                                            value={field.value || ''}
                                            onChange={field.onChange}
                                            modules={modules}
                                            formats={formats}
                                            className="h-full"
                                        />
                                    )}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">What's Included</label>
                            <div className="border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 [&_.ql-toolbar]:!border-0 [&_.ql-toolbar]:!border-b [&_.ql-toolbar]:!border-gray-200 [&_.ql-toolbar]:rounded-t-lg [&_.ql-container]:!border-0 [&_.ql-container]:rounded-b-lg [&_.ql-container]:h-48 [&_.ql-editor]:overflow-y-auto">
                                <Controller
                                    name="includeItems"
                                    control={control}
                                    render={({ field }) => (
                                        <ReactQuill
                                            theme="snow"
                                            value={field.value || ''}
                                            onChange={field.onChange}
                                            modules={modules}
                                            formats={formats}
                                            className="h-full"
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 mt-8 pt-4 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:ring-4 focus:ring-indigo-300 disabled:opacity-50 flex items-center"
                        >
                            <span className="flex items-center">
                                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                {puja ? 'Update Puja' : 'Create Puja'}
                            </span>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddEditPujaModal;
